/**
 * 사용자 수정 모달 컴포넌트
 * - 이름, 이메일, 전화번호, 비밀번호, 학년, 학교, 담당강사, 활성 상태 수정
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, message } from 'antd';
import { userService } from '../../services/userService';
import type { User, UserUpdateRequest } from '../../types/user';

interface EditUserModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [instructors, setInstructors] = useState<User[]>([]);

  /**
   * 강사 목록 로드 (학생인 경우만)
   */
  useEffect(() => {
    if (user?.role === 'student' && open) {
      loadInstructors();
    }
  }, [user, open]);

  const loadInstructors = async () => {
    try {
      const response = await userService.getUsers({
        role: 'instructor',
        is_active: true,
        page_size: 100,
      });
      setInstructors(response.users);
    } catch (error) {
      console.error('강사 목록 로드 실패:', error);
    }
  };

  /**
   * 사용자 정보가 변경되면 폼에 반영
   */
  useEffect(() => {
    if (user && open) {
      form.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phone,
        grade: user.grade,
        school: user.school,
        instructor_id: user.instructor_ids?.[0],
        is_active: user.is_active,
      });
    }
  }, [user, open, form]);

  /**
   * 모달 닫기 처리
   */
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  /**
   * 사용자 수정 제출
   */
  const handleSubmit = async () => {
    if (!user) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: UserUpdateRequest = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        password: values.password,
        grade: values.grade,
        school: values.school,
        instructor_id: values.instructor_id,
        is_active: values.is_active,
      };

      // 비밀번호가 입력되지 않았으면 제거
      if (!values.password) {
        delete data.password;
      }

      await userService.updateUser(user.id, data);
      message.success('사용자 정보가 수정되었습니다');
      handleClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('사용자 수정 실패');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="사용자 수정"
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      okText="수정"
      cancelText="취소"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="이름"
          name="name"
          rules={[
            { required: true, message: '이름을 입력하세요' },
            { min: 1, max: 100, message: '이름은 1-100자여야 합니다' },
          ]}
        >
          <Input placeholder="홍길동" />
        </Form.Item>

        <Form.Item
          label="이메일"
          name="email"
          rules={[
            { required: true, message: '이메일을 입력하세요' },
            { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
          ]}
        >
          <Input placeholder="user@example.com" />
        </Form.Item>

        <Form.Item
          label="비밀번호 변경"
          name="password"
          help="변경하지 않으려면 비워두세요"
        >
          <Input.Password placeholder="새 비밀번호 (6자 이상)" />
        </Form.Item>

        <Form.Item
          label="전화번호"
          name="phone"
          rules={[
            { pattern: /^\d{3}-\d{4}-\d{4}$/, message: '010-0000-0000 형식으로 입력하세요' },
          ]}
        >
          <Input placeholder="010-0000-0000" />
        </Form.Item>

        {/* 학생인 경우만 학년/학교/담당강사 표시 */}
        {user?.role === 'student' && (
          <>
            <Form.Item
              label="학년"
              name="grade"
            >
              <Select
                placeholder="학년을 선택하세요"
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value={1}>초1</Select.Option>
                <Select.Option value={2}>초2</Select.Option>
                <Select.Option value={3}>초3</Select.Option>
                <Select.Option value={4}>초4</Select.Option>
                <Select.Option value={5}>초5</Select.Option>
                <Select.Option value={6}>초6</Select.Option>
                <Select.Option value={7}>중1</Select.Option>
                <Select.Option value={8}>중2</Select.Option>
                <Select.Option value={9}>중3</Select.Option>
                <Select.Option value={10}>고1</Select.Option>
                <Select.Option value={11}>고2</Select.Option>
                <Select.Option value={12}>고3</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="학교"
              name="school"
            >
              <Input placeholder="학교명" />
            </Form.Item>

            <Form.Item
              label="담당 강사"
              name="instructor_id"
            >
              <Select
                placeholder="담당 강사를 선택하세요"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {instructors.map((instructor) => (
                  <Select.Option key={instructor.id} value={instructor.id}>
                    {instructor.name} ({instructor.email})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}

        <Form.Item
          label="활성 상태"
          name="is_active"
          valuePropName="checked"
        >
          <Switch checkedChildren="활성" unCheckedChildren="비활성" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
